import { toast } from "sonner";

/**
 * Shared phone + WhatsApp action helpers (Feb 2026).
 *
 * Used by Student Profile quick-contact, Batch invite, and Pickup QR flows.
 * Centralising here ensures:
 *   • a single 10-digit / country-code normalisation rule
 *   • consistent error toasts ("Valid phone number not available")
 *   • consistent desktop fallback for `tel:` (copy to clipboard — Q7=c)
 */

/**
 * Normalise a raw phone string for wa.me / tel: usage.
 *
 * - Strips everything except digits + leading "+".
 * - Strips a leading "+" or "00" (wa.me wants bare digits, tel: accepts both).
 * - If exactly 10 digits and a defaultCountryCode is provided, prepends the
 *   country code. e.g. ("9876543210", "+91") → "919876543210"
 * - Returns "" when no usable digits remain — the caller must treat empty
 *   as "no valid phone".
 */
export const normalisePhone = (raw, defaultCountryCode) => {
  let s = String(raw || "").replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+")) s = s.slice(1);
  else if (s.startsWith("00")) s = s.slice(2);
  const cc = String(defaultCountryCode || "").replace(/[^\d]/g, "");
  if (s.length === 10 && cc) return cc + s;
  return s;
};

/**
 * Desktop detector. We treat tablets as mobile for the `tel:` flow
 * because most tablets have a phone app or VoIP integration.
 */
const isMobile = () =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

/**
 * Open the native dialer on mobile, copy to clipboard on desktop (Q7=c).
 * Always shows a single feedback toast on desktop so the admin knows what
 * happened (silence is worse than a confusing tap).
 */
export const callPhone = (rawPhone, { defaultCountryCode } = {}) => {
  const phone = normalisePhone(rawPhone, defaultCountryCode);
  if (!phone) {
    toast.error("Valid phone number not available");
    return;
  }
  if (isMobile()) {
    // Mobile: invoke native dialer. tel: links don't need a country code
    // prefix to work, but the normalised value is fine either way.
    window.location.href = `tel:+${phone}`;
    return;
  }
  // Desktop: copy to clipboard with a friendly toast.
  try {
    navigator.clipboard.writeText(`+${phone}`);
    toast.success("Phone number copied to clipboard", {
      description: `+${phone} — paste into your phone app or VoIP.`,
    });
  } catch {
    toast.message(`+${phone}`, {
      description: "Copy this number to your phone app.",
    });
  }
};

/**
 * Open WhatsApp deep link (chat with text). Uses wa.me — works on Android
 * native app, iOS native app, and desktop WhatsApp Web.
 *
 * Synchronous window.open keeps user-activation intact for popup blockers.
 */
export const openWhatsApp = (rawPhone, message = "Hi", { defaultCountryCode } = {}) => {
  const phone = normalisePhone(rawPhone, defaultCountryCode);
  if (!phone) {
    toast.error("Valid phone number not available");
    return;
  }
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message || "")}`;
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    toast.message("Pop-up was blocked", {
      description: "Tap below to open WhatsApp.",
      action: {
        label: "Open WhatsApp",
        onClick: () => window.open(url, "_blank", "noopener,noreferrer"),
      },
    });
  }
};

/**
 * Build the option list for ContactPickerDialog from a student-like row.
 * Returns up to 2 entries (Student / Guardian). Omits any entry whose phone
 * doesn't have at least 10 digits — operational signal of "no valid phone".
 *
 * Each entry: { key, role, name, phone } — consumed by the 3-line picker row.
 *   • role  — "Student" / "Parent/Guardian" (small caps tag at top)
 *   • name  — person's actual name (falls back to the role label when unknown)
 *   • phone — exactly the raw phone string (formatting is the picker's job)
 *
 * Order: Student first, Guardian second — consistent everywhere (Q2=a).
 */
export const buildContactOptions = ({
  studentName,
  studentPhone,
  guardianName,
  guardianPhone,
}) => {
  const valid = (p) => String(p || "").replace(/\D/g, "").length >= 10;
  const opts = [];
  if (valid(studentPhone)) {
    opts.push({
      key: "student",
      role: "Student",
      name: studentName || "Student",
      phone: studentPhone,
    });
  }
  if (valid(guardianPhone)) {
    opts.push({
      key: "guardian",
      role: "Parent/Guardian",
      name: guardianName || "Parent/Guardian",
      phone: guardianPhone,
    });
  }
  return opts;
};

/**
 * One-call helper for the common pattern:
 *   • exactly one valid contact → fire action directly (no popup)
 *   • two valid contacts → set picker state so the dialog opens
 *   • zero valid contacts → toast.error
 *
 * Returns either { picker: {...} } (caller should setPicker(...)) or null
 * when the action has already been fired.
 */
export const resolveContactAction = ({
  studentName,
  studentPhone,
  guardianName,
  guardianPhone,
  onPick,
}) => {
  const options = buildContactOptions({ studentName, studentPhone, guardianName, guardianPhone });
  if (options.length === 0) {
    toast.error("Valid phone number not available");
    return null;
  }
  if (options.length === 1) {
    // Skip the picker entirely (Q5=a).
    const phone = options[0].key === "student" ? studentPhone : guardianPhone;
    const name = options[0].key === "student" ? studentName : guardianName;
    onPick?.({ key: options[0].key, phone, name });
    return null;
  }
  return { options };
};
